import React, { useState, useEffect } from "react";
import {
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
} from "@mui/material";
import { FileText } from "lucide-react";
import { useFormik } from "formik";
import * as yup from "yup";

import {
  createDocumentTypeAction,
  updateDocumentTypeAction,
} from "../../Actions/DocumentTypeActions";

import { MyCloseIcon } from "../../Assets/svg/MyIcons";
import { containsBadWords } from "../../utils/badWord";
import "../../components/Common/FormModal.css";

const getChangedPlanFields = (oldData, newData) => {
  const changes = {};
  if (!oldData) return changes;

  Object.keys(newData).forEach((key) => {
    const oldVal = oldData[key];
    const newVal = newData[key];

    const oldIsEmpty = oldVal === null || oldVal === undefined || oldVal === "";
    const newIsEmpty = newVal === null || newVal === undefined || newVal === "";

    if (oldIsEmpty && newIsEmpty) return;

    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes[key] = {
        old: oldVal,
        new: newVal,
      };
    }
  });

  return changes;
};

const AddEditDocumentType = ({
  open,
  handleClose,
  editData,
  setSnackbar,
  refresh,
}) => {
  const [loading, setLoading] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(null);
  const [changedFields, setChangedFields] = useState({});

  const formik = useFormik({
    initialValues: {
      code: editData?.code || "",
      name: editData?.name || "",
      provider: editData?.provider || "SUMSUB",
      provider_code: editData?.provider_code || "",
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: yup.object({
      code: yup
        .string()
        .trim()
        .required("Code is required")
        .test(
          "bad-word",
          "Code contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        ),

      name: yup
        .string()
        .trim()
        .required("Name is required")
        .test(
          "bad-word",
          "Name contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        ),

      provider: yup
        .string()
        .trim()
        .test(
          "bad-word",
          "Provider contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        ),

      provider_code: yup
        .string()
        .trim()
        .required("Provider code is required")
        .test(
          "bad-word",
          "Provider code contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        ),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = { ...values };
        if (editData) {
          payload.changedFields = JSON.stringify(Object.keys(changedFields));
          payload.comparisonData = JSON.stringify(changedFields);
          await updateDocumentTypeAction(editData.id, payload);

          setSnackbar({
            open: true,
            message: "Document type updated successfully",
            severity: "success",
          });
        } else {
          await createDocumentTypeAction(values);

          setSnackbar({
            open: true,
            message: "Document type created successfully",
            severity: "success",
          });
        }

        handleClose();
        refresh();
      } catch (err) {
        setSnackbar({
          open: true,
          message: err?.response?.data?.message,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (editData) {
      const initialValuesToSet = {
        code: editData?.code || "",
        name: editData?.name || "",
        provider: editData?.provider || "SUMSUB",
        provider_code: editData?.provider_code || "",
      };
      setInitialFormValues(initialValuesToSet);
    }
  }, [editData]);

  useEffect(() => {
    if (!editData || !initialFormValues) return;

    const changed = getChangedPlanFields(initialFormValues, formik.values);
    setChangedFields(changed);
  }, [formik.values, initialFormValues, editData]);

  const isEdit = Boolean(editData);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "fm-dialog" }}
    >
      <div className="fm-header">
        <span className="fm-header-icon" aria-hidden>
          <FileText size={20} />
        </span>
        <div className="fm-header-copy">
          <h2>
            {isEdit
              ? "Update KYC Country Document Type"
              : "Add KYC Country Document Type"}
          </h2>
          <p>
            {isEdit
              ? "Adjust the code, name, and provider mapping for this type."
              : "Define a document type and how it maps to the verification provider."}
          </p>
        </div>
        <IconButton
          aria-label="close"
          className="fm-close"
          onClick={handleClose}
        >
          <MyCloseIcon />
        </IconButton>
      </div>

      <form onSubmit={formik.handleSubmit} className="fm-form">
        <div className="fm-body">
          <section className="fm-section">
            <div className="fm-section-head">
              <h3>Document details</h3>
              <p>Internal label and unique code used across KYC flows.</p>
            </div>
            <div className="fm-card">
              <div className="fm-grid">
                <div className="fm-field">
                  <label htmlFor="doc-code">
                    Code<span className="fm-req">*</span>
                  </label>
                  <TextField
                    id="doc-code"
                    name="code"
                    placeholder="e.g. PASSPORT"
                    size="small"
                    fullWidth
                    value={formik.values.code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.code && Boolean(formik.errors.code)}
                    helperText={formik.touched.code && formik.errors.code}
                  />
                </div>

                <div className="fm-field">
                  <label htmlFor="doc-name">
                    Name<span className="fm-req">*</span>
                  </label>
                  <TextField
                    id="doc-name"
                    name="name"
                    placeholder="e.g. Passport"
                    size="small"
                    fullWidth
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="fm-section">
            <div className="fm-section-head">
              <h3>Provider mapping</h3>
              <p>How this type is identified by the verification provider.</p>
            </div>
            <div className="fm-card">
              <div className="fm-grid">
                <div className="fm-field">
                  <label htmlFor="doc-provider">Provider</label>
                  <TextField
                    id="doc-provider"
                    name="provider"
                    placeholder="e.g. SUMSUB"
                    size="small"
                    fullWidth
                    value={formik.values.provider}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.provider && Boolean(formik.errors.provider)
                    }
                    helperText={
                      formik.touched.provider && formik.errors.provider
                    }
                  />
                </div>

                <div className="fm-field">
                  <label htmlFor="doc-provider-code">
                    Provider code<span className="fm-req">*</span>
                  </label>
                  <TextField
                    id="doc-provider-code"
                    name="provider_code"
                    placeholder="Provider-side document code"
                    size="small"
                    fullWidth
                    value={formik.values.provider_code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.provider_code &&
                      Boolean(formik.errors.provider_code)
                    }
                    helperText={
                      formik.touched.provider_code &&
                      formik.errors.provider_code
                    }
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="fm-footer">
          <button type="button" className="fm-btn" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="fm-btn is-primary"
            disabled={loading}
          >
            {isEdit ? "Update" : "Add"}
            {loading && <CircularProgress size={16} color="inherit" />}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default AddEditDocumentType;
